import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTabs from '@/components/PricingTabs';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Shield, Search, FileCheck, AlertCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TrademarkRegistration() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Shield,
      title: 'Legal Protection',
      description: 'Gain exclusive rights to your brand name and logo nationwide.',
    },
    {
      icon: Search,
      title: 'Brand Recognition',
      description: 'Build credibility and trust with your customers through official registration.',
    },
    {
      icon: FileCheck,
      title: 'Asset Value',
      description: 'Increase your business value with registered intellectual property.',
    },
    {
      icon: AlertCircle,
      title: 'Prevent Infringement',
      description: 'Stop others from using similar marks that could confuse customers.',
    },
  ];

  const faqs = [
    {
      question: 'What is a trademark?',
      answer: 'A trademark is a recognizable sign, design, or expression that identifies and distinguishes products or services of a particular source from those of others. It can be a word, phrase, symbol, design, or combination of these elements.',
    },
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The entire process typically takes 8-12 months from the initial filing to final registration. However, this timeline can vary depending on the complexity of your application and any potential office actions from the USPTO.',
    },
    {
      question: 'How long does trademark protection last?',
      answer: 'Once registered, a trademark can last indefinitely as long as you continue to use it in commerce and file the necessary maintenance documents. You must file maintenance documents between the 5th and 6th year after registration, and then every 10 years thereafter.',
    },
    {
      question: 'What can be trademarked?',
      answer: 'You can trademark brand names, logos, slogans, product names, service names, and even distinctive sounds, colors, or packaging when they identify and distinguish your products or services from others.',
    },
    {
      question: 'Do I need an attorney to file a trademark?',
      answer: 'While not required, having professional assistance significantly increases your chances of success. Our experienced team handles the entire process, from comprehensive searches to responding to office actions, ensuring your application meets all legal requirements.',
    },
    {
      question: 'What is the difference between TM and ® symbols?',
      answer: 'The TM symbol can be used with any mark you claim as a trademark, even if not registered. The ® symbol can only be used after your trademark is officially registered with the USPTO. Using ® before registration can result in penalties.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="trademark-registration-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="service-hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="service-hero-title"
            >
              Trademark Registration
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8" data-testid="service-hero-subtitle">
              Protect your brand identity with comprehensive trademark registration services. Secure exclusive rights to your name, logo, and slogan.
            </p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary"
              data-testid="service-hero-cta"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-abstract-bg" data-testid="service-benefits-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Register Your Trademark?
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Trademark registration offers numerous advantages for your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in" data-testid={`benefit-${index}`}>
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <PricingTabs />

      {/* FAQ Section */}
      <section className="section section-abstract-shapes" data-testid="service-faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Get answers to common questions about trademark registration.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger
                    className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white" data-testid="service-cta-section">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Protect Your Brand?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Don't wait until it's too late. Secure your trademark today and safeguard your business's future.
          </p>
          <button
            onClick={() => setShowDialog(true)}
            className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl"
            data-testid="service-cta-btn"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="trademark_registration" />

      <Footer />
    </div>
  );
}